import React, { useState, useEffect } from 'react';
import { settingsAPI } from '../../lib/api';
import { Save, Upload, Trash2, Building2, Phone, Mail, Globe, FileText, DollarSign } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const CompanySettings = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    
    const [formData, setFormData] = useState({
        company_name: '',
        company_address: '',
        company_phone: '',
        company_email: '',
        company_website: '',
        tax_identification_number: '',
        currency: 'BDT'
    });
    
    const [logoPreview, setLogoPreview] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [existingLogo, setExistingLogo] = useState('');

    const currencies = [
        { code: 'BDT', name: 'Bangladeshi Taka (BDT)' },
        { code: 'USD', name: 'US Dollar (USD)' },
        { code: 'EUR', name: 'Euro (EUR)' },
        { code: 'GBP', name: 'British Pound (GBP)' },
        { code: 'INR', name: 'Indian Rupee (INR)' },
        { code: 'PKR', name: 'Pakistani Rupee (PKR)' },
    ];

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const response = await settingsAPI.getCompanySettings();
            if (response.data.success) {
                const data = response.data.data;
                setFormData({
                    company_name: data.company_name || '',
                    company_address: data.company_address || '',
                    company_phone: data.company_phone || '',
                    company_email: data.company_email || '',
                    company_website: data.company_website || '',
                    tax_identification_number: data.tax_identification_number || '',
                    currency: data.currency || 'BDT'
                });
                if (data.company_logo) {
                    setExistingLogo(data.company_logo);
                    // Use the API base URL to construct the full storage URL
                    const baseUrl = API_BASE_URL.replace('/api', '');
                    setLogoPreview(`${baseUrl}/storage/${data.company_logo}`);
                }
            }
        } catch (err) {
            setError('Failed to load settings');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/svg+xml'];
            if (!validTypes.includes(file.type)) {
                setError('Please select a valid image file (JPEG, PNG, JPG, GIF, SVG)');
                return;
            }
            
            // Validate file size (max 2MB)
            if (file.size > 2 * 1024 * 1024) {
                setError('File size must be less than 2MB');
                return;
            }

            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
            setError('');
        }
    };

    const handleRemoveLogo = async () => {
        if (existingLogo) {
            try {
                await settingsAPI.deleteLogo();
                setExistingLogo('');
                setLogoPreview(null);
                setLogoFile(null);
            } catch (err) {
                setError('Failed to remove logo');
            }
        } else {
            setLogoPreview(null);
            setLogoFile(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');
        setSaving(true);

        try {
            // First, save text fields
            await settingsAPI.saveCompanySettings(formData);

            // Then, upload logo if selected
            if (logoFile) {
                const formDataLogo = new FormData();
                formDataLogo.append('logo', logoFile);
                await settingsAPI.uploadLogo(formDataLogo);
            }

            setSuccessMessage('Company settings saved successfully!');
            setLogoFile(null);
            
            // Refresh to get the new logo URL
            await fetchSettings();
            
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err.response?.data?.message || err.response?.data?.error || 'Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto">
            <div className="mb-6">
                <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Company Settings</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">Manage your company information</p>
            </div>

            {error && (
                <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                    {error}
                    <button 
                        onClick={() => setError('')} 
                        className="float-right text-red-700 hover:text-red-900"
                    >
                        ✕
                    </button>
                </div>
            )}

            {successMessage && (
                <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                    {successMessage}
                </div>
            )}

            <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-700 rounded-lg shadow-lg p-6">
                {/* Logo Section */}
                <div className="mb-8">
                    <h2 className="text-lg font-medium text-gray-800 dark:text-gray-100 mb-4 flex items-center">
                        <Building2 className="w-5 h-5 mr-2" />
                        Company Logo
                    </h2>
                    <div className="flex items-center space-x-6">
                        <div className="flex-shrink-0">
                            {logoPreview ? (
                                <div className="relative">
                                    <img 
                                        src={logoPreview} 
                                        alt="Company Logo" 
                                        className="h-32 w-32 object-contain rounded-lg border border-gray-300 dark:border-slate-500"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleRemoveLogo}
                                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            ) : (
                                <div className="h-32 w-32 rounded-lg border-2 border-dashed border-gray-300 dark:border-slate-500 flex items-center justify-center">
                                    <Building2 className="w-12 h-12 text-gray-400" />
                                </div>
                            )}
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Upload Logo
                            </label>
                            <div className="flex items-center space-x-3">
                                <label className="cursor-pointer inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg">
                                    <Upload className="w-4 h-4 mr-2" />
                                    Choose File
                                    <input 
                                        type="file" 
                                        accept="image/*"
                                        onChange={handleLogoChange}
                                        className="hidden"
                                    />
                                </label>
                                <span className="text-sm text-gray-500 dark:text-gray-400">
                                    Max 2MB (JPEG, PNG, JPG, GIF, SVG)
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Basic Info Section */}
                <div className="mb-8">
                    <h2 className="text-lg font-medium text-gray-800 dark:text-gray-100 mb-4 flex items-center">
                        <Building2 className="w-5 h-5 mr-2" />
                        Basic Information
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Company Name
                            </label>
                            <input
                                type="text"
                                name="company_name"
                                value={formData.company_name}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200"
                                placeholder="Enter company name"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Currency
                            </label>
                            <div className="relative">
                                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <select
                                    name="currency"
                                    value={formData.currency}
                                    onChange={handleChange}
                                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200"
                                >
                                    {currencies.map(curr => (
                                        <option key={curr.code} value={curr.code}>
                                            {curr.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Address Section */}
                <div className="mb-8">
                    <h2 className="text-lg font-medium text-gray-800 dark:text-gray-100 mb-4">
                        Contact Information
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Address
                            </label>
                            <textarea
                                name="company_address"
                                value={formData.company_address}
                                onChange={handleChange}
                                rows="3"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200"
                                placeholder="Enter company address"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Phone Number
                            </label>
                            <div className="relative">
                                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    name="company_phone"
                                    value={formData.company_phone}
                                    onChange={handleChange}
                                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200"
                                    placeholder="+880 1XXXXXXXXX"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Email Address
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="email"
                                    name="company_email"
                                    value={formData.company_email}
                                    onChange={handleChange}
                                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200"
                                    placeholder="company@example.com"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Website
                            </label>
                            <div className="relative">
                                <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="url"
                                    name="company_website"
                                    value={formData.company_website}
                                    onChange={handleChange}
                                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200"
                                    placeholder="https://example.com"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Tax Identification Number (TIN)
                            </label>
                            <div className="relative">
                                <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    name="tax_identification_number"
                                    value={formData.tax_identification_number}
                                    onChange={handleChange}
                                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200"
                                    placeholder="Enter TIN number"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end">
                    <button
                        type="submit"
                        disabled={saving}
                        className="inline-flex items-center px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {saving ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Saving...
                            </>
                        ) : (
                            <>
                                <Save className="w-4 h-4 mr-2" />
                                Save Settings
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CompanySettings;
